import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TableCell,
  Chip,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import TrackingBase from '../../components/TrackingBase/TrackingBase';
import LocationViewer from '../../components/LocationViewer/LocationViewer';
import { patientTrackingService } from '../../services/patientTrackingService';
import { devicesAPI } from '../../services/api';

const PatientTracking = () => {
  const [patients, setPatients] = useState([]);
  const [wristbands, setWristbands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationDialog, setLocationDialog] = useState({ open: false, patient: null });

  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await patientTrackingService.getPatients();
      const patientsData = response.data?.data || response.data || [];
      console.log('Patients data from API:', patientsData);
      if (patientsData.length > 0) {
        console.log('First patient sample:', patientsData[0]);
      }
      setPatients(Array.isArray(patientsData) ? patientsData : []);
    } catch (error) {
      console.error('Error fetching patients:', error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
    loadWristbands();
    const interval = setInterval(fetchPatients, 10000);
    return () => clearInterval(interval);
  }, [fetchPatients]);

  const loadWristbands = async () => {
    try {
      const response = await devicesAPI.getAll();
      const devicesData = Array.isArray(response?.data?.data) ? response.data.data : 
                         Array.isArray(response?.data) ? response.data : [];
      const wristbandsOnly = devicesData.filter(device => device.type === 'wristband' && device.ble_id);
      setWristbands(wristbandsOnly);
    } catch (error) {
      console.warn('Failed to load wristbands:', error.message);
      setWristbands([]);
    }
  };

  // Handlers para o componente base
  const handleCreatePatient = async (formData) => {
    const submitData = { ...formData, restricted_zones: JSON.stringify(formData.restricted_zones || []) };
    return await patientTrackingService.createPatient(submitData);
  };

  const handleEditPatient = async (patientId, formData) => {
    const submitData = { ...formData, restricted_zones: JSON.stringify(formData.restricted_zones || []) };
    return await patientTrackingService.updatePatient(patientId, submitData);
  };

  const handleDeletePatient = async (patientId) => {
    return await patientTrackingService.deletePatient(patientId);
  };

  const handleAssociateBeacon = async (patientId, beaconData) => {
    return await patientTrackingService.associateBeacon(patientId, beaconData);
  };

  const handleConfigureRestrictions = async (patientId, restrictions) => {
    return await patientTrackingService.updateRestrictions(patientId, restrictions);
  };

  const handleViewLocation = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    if (patient) {
      setLocationDialog({ open: true, patient });
    }
  };

  const handleCloseLocationDialog = () => {
    setLocationDialog({ open: false, patient: null });
  };

  // Utilitários
  const getStatusColor = (timestamp) => {
    if (!timestamp) return 'default';
    const now = new Date();
    const lastUpdate = new Date(timestamp);
    const diffMinutes = (now - lastUpdate) / (1000 * 60);
    
    if (diffMinutes < 5) return 'success';
    if (diffMinutes < 15) return 'warning';
    return 'error';
  };

  const getStatusText = (timestamp) => {
    if (!timestamp) return 'Sem localização';
    const now = new Date();
    const lastUpdate = new Date(timestamp);
    const diffMinutes = Math.floor((now - lastUpdate) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Agora';
    if (diffMinutes < 60) return `${diffMinutes}min atrás`;
    return `${Math.floor(diffMinutes / 60)}h atrás`;
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'success';
    }
  };

  const getMobilityText = (restriction) => {
    switch (restriction) {
      case 'bed_only': return 'Restrito ao leito';
      case 'room_only': return 'Restrito ao quarto';
      case 'ward_only': return 'Restrito à ala';
      default: return 'Livre';
    }
  };

  // Métricas para o dashboard
  const getMetrics = (patients) => ({
    total: {
      label: 'Total de Pacientes',
      value: patients.length,
    },
    active: {
      label: 'Ativos Agora',
      value: patients.filter(p => getStatusColor(p.last_location_update) === 'success').length,
      color: 'success.main',
    },
    highRisk: {
      label: 'Alto Risco',
      value: patients.filter(p => p.fall_risk_level === 'high' || p.fall_risk_level === 'critical').length,
      color: 'error.main',
    },
    restricted: {
      label: 'Com Restrições',
      value: patients.filter(p => p.mobility_restriction !== 'none').length,
      color: 'warning.main',
    },
  });

  // Renderizadores de formulário
  const renderCreateEditForm = (selectedItem, tabType, onSubmit) => {
    const handleSubmit = (e) => {
      e.preventDefault();
      const formDataToSubmit = new FormData(e.target);
      const data = {
        external_patient_id: formDataToSubmit.get('external_patient_id') || '',
        medical_record_number: formDataToSubmit.get('medical_record_number') || '',
        current_room: formDataToSubmit.get('current_room') || '',
        assigned_bed: formDataToSubmit.get('assigned_bed') || '',
        fall_risk_level: formDataToSubmit.get('fall_risk_level') || 'low',
        emergency_protocol: formDataToSubmit.get('emergency_protocol') || '',
        notes: formDataToSubmit.get('notes') || '',
        enable_zone_alerts: formDataToSubmit.get('enable_zone_alerts') === 'on',
        enable_fall_detection: formDataToSubmit.get('enable_fall_detection') === 'on',
        enable_bed_exit_alerts: formDataToSubmit.get('enable_bed_exit_alerts') === 'on',
        mobility_restriction: formDataToSubmit.get('mobility_restriction') || 'none'
      };
      onSubmit(data);
    };

    if (tabType === 'basic') {
      return (
        <form id="tracking-form" onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="external_patient_id"
                label="ID Externo (PEP)"
                fullWidth
                defaultValue={selectedItem?.external_patient_id || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="medical_record_number"
                label="Número do Prontuário"
                fullWidth
                defaultValue={selectedItem?.medical_record_number || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="current_room"
                label="Quarto Atual"
                fullWidth
                defaultValue={selectedItem?.current_room || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="assigned_bed"
                label="Leito Designado"
                fullWidth
                defaultValue={selectedItem?.assigned_bed || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Nível de Risco de Queda</InputLabel>
                <Select
                  name="fall_risk_level"
                  defaultValue={selectedItem?.fall_risk_level || 'low'}
                  label="Nível de Risco de Queda"
                >
                  <MenuItem value="low">Baixo</MenuItem>
                  <MenuItem value="medium">Médio</MenuItem>
                  <MenuItem value="high">Alto</MenuItem>
                  <MenuItem value="critical">Crítico</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="emergency_protocol"
                label="Protocolo de Emergência"
                fullWidth
                defaultValue={selectedItem?.emergency_protocol || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Observações"
                fullWidth
                multiline
                rows={3}
                defaultValue={selectedItem?.notes || ''}
              />
            </Grid>
          </Grid>
        </form>
      );
    }

    if (tabType === 'rtls') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Configurações de Rastreamento
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="enable_zone_alerts"
                  defaultChecked={Boolean(selectedItem?.enable_zone_alerts)}
                />
              }
              label="Alertas de Zona"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="enable_fall_detection"
                  defaultChecked={Boolean(selectedItem?.enable_fall_detection)}
                />
              }
              label="Detecção de Queda"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="enable_bed_exit_alerts"
                  defaultChecked={Boolean(selectedItem?.enable_bed_exit_alerts)}
                />
              }
              label="Alertas de Saída do Leito"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Restrição de Mobilidade</InputLabel>
              <Select
                name="mobility_restriction"
                defaultValue={selectedItem?.mobility_restriction || 'none'}
                label="Restrição de Mobilidade"
              >
                <MenuItem value="none">Nenhuma</MenuItem>
                <MenuItem value="bed_only">Restrito ao Leito</MenuItem>
                <MenuItem value="room_only">Restrito ao Quarto</MenuItem>
                <MenuItem value="ward_only">Restrito à Ala</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const renderBeaconForm = (selectedItem, availableDevices, onSubmit) => {
    const handleSubmit = (e) => {
      e.preventDefault();
      const formDataToSubmit = new FormData(e.target);
      const data = {
        beacon_id: formDataToSubmit.get('beacon_id') || '',
        beacon_type: 'wristband',
        has_accelerometer: false,
        enable_fall_detection: formDataToSubmit.get('enable_fall_detection') === 'on'
      };
      onSubmit(data);
    };

    return (
      <form id="tracking-form" onSubmit={handleSubmit}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Pulseira BLE</InputLabel>
              <Select
                name="beacon_id"
                defaultValue={selectedItem?.beacon_id || ''}
                label="Pulseira BLE"
              >
                <MenuItem value="">
                  <em>Selecione uma pulseira</em>
                </MenuItem>
                {Array.isArray(availableDevices) && availableDevices.map((wristband) => {
                  const capabilities = wristband.capabilities || [];
                  const hasAccelerometer = capabilities.includes('accelerometer');
                  const hasHeartRate = capabilities.includes('heart_rate');
                  return (
                    <MenuItem key={wristband.id} value={wristband.ble_id}>
                      <Box>
                        <Typography variant="body2">
                          {wristband.ble_id} - {wristband.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {hasAccelerometer && 'Detecção de queda'}
                          {hasAccelerometer && hasHeartRate && ' • '}
                          {hasHeartRate && 'Monitor cardíaco'}
                          {!hasAccelerometer && !hasHeartRate && 'Localização apenas'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="enable_fall_detection"
                  defaultChecked={Boolean(selectedItem?.enable_fall_detection)}
                />
              }
              label="Ativar Detecção de Queda"
            />
          </Grid>
        </Grid>
      </form>
    );
  };

  // Renderizadores para o componente base
  const renderTableColumns = () => [
    <TableCell key="record">Prontuário</TableCell>,
    <TableCell key="room">Quarto/Leito</TableCell>,
    <TableCell key="location">Localização</TableCell>,
    <TableCell key="risk">Risco</TableCell>,
    <TableCell key="mobility">Mobilidade</TableCell>,
    <TableCell key="status">Status</TableCell>,
  ];

  const renderTableRow = (patient) => [
    <TableCell key="record">
      <Box>
        <Typography variant="body2" fontWeight="bold">
          {patient.medical_record_number || 'N/A'}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {patient.external_patient_id || 'N/A'}
        </Typography>
      </Box>
    </TableCell>,
    <TableCell key="room">
      <Box>
        <Typography variant="body2">{patient.current_room || 'N/A'}</Typography>
        <Typography variant="caption" color="textSecondary">
          {patient.assigned_bed || 'N/A'}
        </Typography>
      </Box>
    </TableCell>,
    <TableCell key="location">
      {((patient.x != null && patient.y != null) || (patient.location_x != null && patient.location_y != null)) ? (
        <Box>
          <Typography variant="body2">
            X: {parseFloat(patient.location_x || patient.x).toFixed(2)}, Y: {parseFloat(patient.location_y || patient.y).toFixed(2)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {patient.last_location_update ? getStatusText(patient.last_location_update) : 'Atualizado'}
          </Typography>
        </Box>
      ) : patient.beacon_id ? (
        <Box>
          <Typography variant="body2" color="textSecondary">
            Aguardando localização...
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Beacon: {patient.beacon_id}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          Sem beacon associado
        </Typography>
      )}
    </TableCell>,
    <TableCell key="risk">
      <Chip
        label={patient.fall_risk_level || 'low'}
        color={getRiskColor(patient.fall_risk_level)}
        size="small"
      />
    </TableCell>,
    <TableCell key="mobility">
      <Typography variant="body2">
        {getMobilityText(patient.mobility_restriction)}
      </Typography>
    </TableCell>,
    <TableCell key="status">
      <Chip
        label={getStatusText(patient.last_location_update)}
        color={getStatusColor(patient.last_location_update)}
        size="small"
      />
    </TableCell>,
  ];

  return (
    <>
      <TrackingBase
        title="Rastreamento de Pacientes"
        data={patients}
        loading={loading}
        onRefresh={fetchPatients}
        onCreateItem={handleCreatePatient}
        onEditItem={handleEditPatient}
        onDeleteItem={handleDeletePatient}
        onAssociateBeacon={handleAssociateBeacon}
        onConfigureRestrictions={handleConfigureRestrictions}
        onViewLocation={handleViewLocation}
        renderTableColumns={renderTableColumns}
        renderTableRow={renderTableRow}
        getMetrics={getMetrics}
        availableDevices={wristbands}
        renderCreateEditForm={renderCreateEditForm}
        renderBeaconForm={renderBeaconForm}
      />
      
      <LocationViewer
        open={locationDialog.open}
        onClose={handleCloseLocationDialog}
        entity={locationDialog.patient}
        entityType="patient"
      />
    </>
  );
};

export default PatientTracking;